import { memo  } from "react";
import dayjs from "dayjs";
import Loader from "js/Component/Loader";
import NoDataFound from "js/Component/NoDataFound";
import { utcToLocal } from "js/helpers/date";

const DataTable = (props) => {
    const {
        data = [],
        columns = [],
        isLoading = false,
        isDuplicating = false,
        onRowClick,
        onEdit,
        onDuplicate,
        onDelete,
        showActions = true,
        isTemplate,
        CopyAsTemplate
    } = props;

    const handleRowClick = (item) => {
        if (onRowClick) {
            onRowClick(item);
        }
    };

    const handleEdit = (e, item) => {
        e.stopPropagation();
        if (onEdit) {
            onEdit(item);
        }
    };

    const handleDuplicate = (e, item) => {
        e.stopPropagation();
        if (onDuplicate) {
            onDuplicate(item);
        }
    };

    const handleDelete = (e, item) => {
        e.stopPropagation();
        if (onDelete) {
            onDelete(item);
        }
    };

    const handleCopyTemplate = (e, item) => {
        e.stopPropagation();
        if (CopyAsTemplate) {
            CopyAsTemplate(item);
        }
    };

    // Helper function to format startTime like the original
    const formatStartTime = (startTime) => {
        if (!startTime) return "-";
        return dayjs(utcToLocal(startTime)).format("DD/MM/YYYY h:mma");
    };

    // Helper function to format time only (HH:MM AM/PM)
    const formatTimeOnly = (startTime) => {
        if (!startTime) return "-";
        return dayjs(utcToLocal(startTime)).format("h:mm A");
    };

    // Helper function to format repeat on days
    const formatRepeatOn = (recurringParam) => {
        if (!recurringParam) return "null";

        try {
            const parsed =
                typeof recurringParam === "string"
                    ? JSON.parse(recurringParam)
                    : recurringParam;

            if (!parsed || !parsed.day || !Array.isArray(parsed.day)) {
                return "null";
            }

            const dayNames = {
                0: "Sun",
                1: "Mon",
                2: "Tue",
                3: "Wed",
                4: "Thu",
                5: "Fri",
                6: "Sat",
            };


            const dayOrder = [1, 2, 3, 4, 5, 6, 0]; // Mon, Tue, Wed, Thu, Fri, Sat, Sun

            const sortedDays = parsed.day
                .sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b))
                .map((dayNum) => dayNames[dayNum])
                .filter(Boolean);

            return sortedDays.length > 0 ? sortedDays.join(", ") : "null";
        } catch (error) {
            console.error("Error parsing recurring param:", error);
            return "null";
        }
    };

    const formatRecipients = (item) => {
        const sentTo = item.sentTo;

        if (!sentTo) return "-";

        if (sentTo === "every_one") {
            return "EveryOne";
        }

        const names = [];

        if (item.devices && Array.isArray(item.devices)) {
            item.devices.forEach(device => {
                if (device.name) {
                    names.push(device.name.trim());
                }
            });
        }

        if (item.group && Array.isArray(item.group)) {
            item.group.forEach(group => {
                if (group.name) {
                    names.push(group.name.trim());
                }
            });
        }

        if (names.length === 0) {
            return "-";
        }


        const joinedNames = names.join(", ");
        const maxLength = 50;

        if (joinedNames.length > maxLength) {
            return joinedNames.substring(0, maxLength) + "...";
        }

        return joinedNames;
    };

    return (
        <div className="row">
            <div className="col-md-12">
                <div className="table-wrap">
                    <table className="table table-responsive-xl table-hover">
                        <thead>
                            <tr key="header">
                                {columns.map((column) => (
                                    <th key={column.key}>{column.label}</th>
                                ))}
                                {showActions && <th>Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td
                                        colSpan={columns.length + (showActions ? 1 : 0)}
                                        className="text-center"
                                    >
                                        <Loader />
                                    </td>
                                </tr>
                            ) : data?.length > 0 ? (
                                data.map((item) => (
                                    <tr key={item.id} onClick={() => handleRowClick(item)} style={{ cursor: onRowClick ? "pointer" : "default" }} >
                                        {columns.map((column) => {
                                            let cellContent;

                                            if (column.render) {
                                                cellContent = column.render(item);
                                            } else if (column.key === "startTime") {
                                                cellContent = formatStartTime(item[column.key]);
                                            } else if (column.key === "repeatOn") {

                                                cellContent = formatRepeatOn(
                                                    item.RecurringParam || item.recurringParam
                                                );
                                            } else if (column.key === "time") {

                                                cellContent = formatTimeOnly(
                                                    item.startTime || item.scheduledOn
                                                );
                                            } else if (column.key === "templateType") {

                                                cellContent =
                                                    item.templateType || item.template_type || "-";
                                            } else if (column.key === "recipients") {
                                                cellContent = formatRecipients(item);
                                            } else {
                                                cellContent = item[column.key] ?? "-";
                                            }

                                            return <td key={column.key}>{cellContent}</td>;
                                        })}
                                        {showActions && (
                                            <td>
                                                {onDuplicate && (
                                                    <button
                                                        className="btn btn-sm me-3"
                                                        onClick={(e) => handleDuplicate(e, item)}
                                                        disabled={isDuplicating}
                                                        style={{
                                                            opacity: isDuplicating ? 0.6 : 1,
                                                            cursor: isDuplicating ? "not-allowed" : "pointer",
                                                        }}
                                                        title={
                                                            isDuplicating ? "Duplicating..." : "Duplicate"
                                                        }
                                                    >
                                                        {isDuplicating ? (
                                                            <i
                                                                className="fa fa-spinner fa-spin"
                                                                aria-hidden="true"
                                                                style={{ color: "#999" }}
                                                            />
                                                        ) : (
                                                            <i
                                                                className="fa fa-copy"
                                                                aria-hidden="true"
                                                                style={{ color: "#000" }}
                                                            />
                                                        )}
                                                    </button>
                                                )}

                                                {onEdit && (
                                                    <button className="btn btn-sm me-3" onClick={(e) => handleEdit(e, item)} disabled={isDuplicating} style={{ opacity: isDuplicating ? 0.6 : 1, cursor: isDuplicating ? "not-allowed" : "pointer", }} >
                                                        <i className="fa fa-pencil" aria-hidden="true" style={{ color: isDuplicating ? "#999" : "#000" }} />
                                                    </button>
                                                )}

                                                {onDelete && (
                                                    <button className="btn btn-sm me-3" onClick={(e) => handleDelete(e, item)} disabled={isDuplicating} style={{ opacity: isDuplicating ? 0.6 : 1, cursor: isDuplicating ? "not-allowed" : "pointer", }} >
                                                        <i className="fa fa-trash" aria-hidden="true" style={{ color: isDuplicating ? "#999" : "#000" }} />
                                                    </button>
                                                )}

                                                {/*{isTemplate ? null*/}
                                                {/*    :*/}
                                                {/*    <>*/}
                                                {/*        <button className="btn btn-sm" onClick={(e) => handleCopyTemplate(e, item)} disabled={isDuplicating} style={{ opacity: isDuplicating ? 0.6 : 1, cursor: isDuplicating ? "not-allowed" : "pointer", }} >*/}
                                                {/*            Copy as Template*/}
                                                {/*        </button> */}
                                                {/*    </>*/}
                                                {/*}*/}
                                            </td>
                                        )}
                                    </tr>
                                ))
                            ) : (
                                <NoDataFound />
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default memo(DataTable);
